import {
  format,
  add,
  sub,
  parse,
  startOfDay,
  getDay,
  endOfDay,
  differenceInMinutes,
} from 'date-fns'

/**
 * Determines the schedule for a specific date for a given staff member,
 * considering exceptions and the regular weekly schedule.
 * @param {Date} date - The selected date.
 * @param {object} staffMember - The staff member object.
 * @returns {object|null} The schedule for the day or null if it's a day off.
 */
export const getScheduleForDate = (date, staffMember) => {
  if (!date || !staffMember) return null

  const dateString = format(date, 'yyyy-MM-dd')

  // 1. Check for an exception on this date. It takes precedence.
  const exception = staffMember.exceptions?.find((ex) => {
    if (!ex.date?.toDate) return false
    return format(ex.date.toDate(), 'yyyy-MM-dd') === dateString
  })

  if (exception) {
    if (exception.isDayOff) {
      return null // Day off
    }
    return {
      timeStart: exception.start,
      timeEnd: exception.end,
      breaks: exception.breaks || [],
    }
  }

  // 2. If no exception, use the general weekly schedule.
  const dayOfWeek = getDay(date) // Sunday = 0, Monday = 1, ...
  const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][dayOfWeek]
  const daySchedule = staffMember.workDays?.[dayKey]

  if (!daySchedule || daySchedule.isDayOff) {
    return null // Day off
  }

  return {
    timeStart: daySchedule.start,
    timeEnd: daySchedule.end,
    breaks: daySchedule.breaks || [],
  }
}

/**
 * Calculates available time slots for a given day.
 * @param {Date} selectedDate - The date for which to calculate slots.
 * @param {number} serviceDuration - The total duration of selected services in minutes.
 * @param {object} scheduleForDay - The schedule object for the selected date.
 * @param {Array} existingBookings - An array of existing bookings for the day.
 * @param {number} minServiceDuration - The minimum duration of a single service.
 * @returns {Array<string>} An array of available time slots in 'HH:mm' format.
 */
export const calculateAvailableTimeSlots = (
  selectedDate,
  serviceDuration,
  scheduleForDay,
  existingBookings,
  minServiceDuration,
) => {
  if (!selectedDate || serviceDuration === 0 || !scheduleForDay) {
    return []
  }

  const { timeStart, timeEnd } = scheduleForDay
  const parseTime = (timeStr) => parse(timeStr, 'HH:mm', selectedDate)

  let busyIntervals = [...existingBookings]
  busyIntervals.sort((a, b) => a.start - b.start)

  const openTimeRanges = []
  let lastBusyEnd = parseTime(timeStart)

  for (const interval of busyIntervals) {
    if (interval.start > lastBusyEnd) {
      openTimeRanges.push({ start: lastBusyEnd, end: interval.start })
    }
    lastBusyEnd = new Date(Math.max(lastBusyEnd, interval.end))
  }

  const workDayEnd = parseTime(timeEnd)
  if (workDayEnd > lastBusyEnd) {
    openTimeRanges.push({ start: lastBusyEnd, end: workDayEnd })
  }

  const slotInterval = minServiceDuration > 0 ? minServiceDuration : 15
  let effectiveMinDuration =
    minServiceDuration === 0 ? slotInterval : minServiceDuration

  return openTimeRanges.flatMap((range) => {
    const slots = []
    let potentialSlotStart = range.start
    const latestStart = sub(range.end, { minutes: serviceDuration })

    while (potentialSlotStart <= latestStart) {
      const potentialSlotEnd = add(potentialSlotStart, {
        minutes: serviceDuration,
      })

      const gapBefore = differenceInMinutes(potentialSlotStart, range.start)
      const gapAfter = differenceInMinutes(range.end, potentialSlotEnd)

      const isGapBeforeValid =
        gapBefore === 0 || gapBefore >= effectiveMinDuration
      const isGapAfterValid = gapAfter === 0 || gapAfter >= effectiveMinDuration

      if (isGapBeforeValid && isGapAfterValid) {
        slots.push(format(potentialSlotStart, 'HH:mm'))
      }
      potentialSlotStart = add(potentialSlotStart, { minutes: slotInterval })
    }
    return slots
  })
}

/**
 * Returns a function to determine if a day should be disabled in a date picker.
 * @param {object} staffMember - The staff member for whom to check the schedule.
 * @returns {Array} An array containing a "before" date and a function for react-day-picker.
 */
export const getDisabledDays = (staffMember) => {
  if (!staffMember) {
    return [{ before: new Date() }, () => true]
  }

  const isDayDisabled = (day) => {
    const schedule = getScheduleForDate(day, staffMember)
    return !schedule
  }

  return [{ before: new Date() }, isDayDisabled]
}
